import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { trackMeta } from '../lib/metaPixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BrochurePage = () => {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  useEffect(() => {
    if (window.scrollY !== 0) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error('Please enter your email address.');
      return;
    }
    if (!consent) {
      toast.error('Please agree to be contacted by the Your Setl team.');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/brochure/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, consent }),
      });

      const data = await res.json();

      if (!res.ok) {
        toast.error(data.detail || 'Something went wrong. Please try again.');
        setLoading(false);
        return;
      }

      // Fire GA4 event
      if (window.gtag) {
        window.gtag('event', 'brochure_download', {
          event_category: 'engagement',
          event_label: 'Brochure PDF Download',
          email_provided: email,
        });
      }

      // Fire Meta Lead event (only when marketing consent was given)
      trackMeta('Lead', { content_name: 'Brochure Download' });

      // Trigger download
      const link = document.createElement('a');
      link.href = `${API_URL}/api/brochure/download/${data.download_token}`;
      link.download = 'YourSetl_Brochure.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setDownloaded(true);
      toast.success('Your brochure is downloading.');
    } catch {
      toast.error('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#f5f1e8] min-h-screen">
      <SEO
        title="Download Our Brochure | Your Setl"
        description="Download the Your Setl brochure and discover our luxury country cabins in Dorset. Eight exclusive cabins on a working farm along the Jurassic Coast."
        keywords="Your Setl brochure, Dorset country cabins brochure, luxury cabin PDF, Jurassic Coast retreat"
        url="https://www.yoursetl.co.uk/brochure"
      />

      {/* Hero */}
      <section className="relative h-[60vh] md:h-[70vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://res.cloudinary.com/dvypx0zh5/image/upload/f_auto,q_auto:best/v1779198166/Doctored_image_nnslm2.png"
            alt="Your Setl country cabin at sunset"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />
        <div className="relative z-10 px-8 md:px-16 pb-12 md:pb-16 max-w-3xl">
          <h1
            className="text-4xl sm:text-5xl font-light text-[#f5f1e8] leading-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Our Brochure
          </h1>
          <div className="w-16 h-px bg-[#f5f1e8]/70 my-4" />
          <p
            className="text-base md:text-lg font-light text-[#f5f1e8] tracking-wide"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Everything about Your Setl in one beautifully crafted document.
          </p>
        </div>
      </section>

      {/* Download Section */}
      <section className="py-16 lg:py-24 px-6">
        <div className="max-w-xl mx-auto">
          {!downloaded ? (
            <>
              <h2
                className="text-2xl md:text-3xl font-light text-[#3a2f28] mb-3 text-center"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Get Your Copy
              </h2>
              <p
                className="text-[#3a2f28]/70 text-center mb-10 text-sm md:text-base"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Enter your email below and we will share the brochure with you right away.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="brochure-email"
                    className="block text-sm font-medium text-[#3a2f28]/80 mb-2"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Email address
                  </label>
                  <input
                    id="brochure-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 bg-white border border-[#d4a574]/40 rounded text-[#3a2f28] placeholder:text-[#3a2f28]/40 focus:outline-none focus:border-[#d4a574] focus:ring-1 focus:ring-[#d4a574] transition-all"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                    data-testid="brochure-email-input"
                  />
                </div>

                <label
                  className="flex items-start gap-3 cursor-pointer group"
                  data-testid="brochure-consent-label"
                >
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-[#d4a574] text-[#d4a574] focus:ring-[#d4a574] cursor-pointer accent-[#d4a574]"
                    data-testid="brochure-consent-checkbox"
                  />
                  <span
                    className="text-sm text-[#3a2f28]/80 leading-relaxed group-hover:text-[#3a2f28]"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    I agree to be contacted by the Your Setl team regarding their country cabin concept.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-8 py-4 bg-[#3a2f28] text-[#f5f1e8] font-light tracking-wide hover:bg-[#d4a574] transition-all duration-300 rounded text-base disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                  data-testid="brochure-download-btn"
                >
                  {loading ? 'Verifying...' : 'Download Brochure'}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-8" data-testid="brochure-success">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#5a7320]/20 flex items-center justify-center">
                <svg className="w-8 h-8 text-[#5a7320]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2
                className="text-2xl md:text-3xl font-light text-[#3a2f28] mb-4"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Your brochure is downloading
              </h2>
              <p
                className="text-[#3a2f28]/70 mb-8 text-sm md:text-base"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Thank you for your interest. We look forward to welcoming you to Dorset.
              </p>
              <button
                onClick={() => {
                  setDownloaded(false);
                  setEmail('');
                  setConsent(false);
                }}
                className="text-[#d4a574] hover:text-[#3a2f28] transition-colors text-sm underline underline-offset-4"
                style={{ fontFamily: 'Inter, sans-serif' }}
                data-testid="brochure-reset-btn"
              >
                Download again with a different email
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BrochurePage;
